import { OverlayModule } from '@angular/cdk/overlay';
import { Component, computed, DestroyRef, ElementRef, inject, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TfsIconDirective } from '@tfs/material/tfs-icon';

import { DataSyncPanelComponent } from '../data-sync-panel/data-sync-panel.component';
import { DATA_SYNC_SOURCES } from '../data-sync-source';

const HOVER_OPEN_DELAY_MS = 300;
const HOVER_CLOSE_DELAY_MS = 200;

@Component({
  selector: 'tfs-data-sync-nav-item',
  imports: [
    OverlayModule,
    MatIconModule,
    MatListModule,
    MatTooltipModule,
    TfsIconDirective,
    DataSyncPanelComponent,
  ],
  templateUrl: './data-sync-nav-item.component.html',
  styleUrl: './data-sync-nav-item.component.scss',
})
export class DataSyncNavItemComponent {
  private readonly destroyRef = inject(DestroyRef);
  protected readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly dataSyncSources = inject(DATA_SYNC_SOURCES);

  readonly isExpanded = input.required<boolean>();

  readonly disabled = input<boolean>(false);
  readonly tooltip = input<string>('');

  private readonly allJobs = computed(() => this.dataSyncSources.flatMap(source => source.jobs()));

  protected readonly activeJobs = computed(() =>
    this.allJobs().filter(job => job.state === 'active')
  );

  protected readonly completedJobs = computed(() =>
    this.allJobs().filter(job => job.state !== 'active')
  );

  protected readonly isOpen = signal(false);

  private hoverOpenTimer: ReturnType<typeof setTimeout> | null = null;
  private hoverCloseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.cancelOpen();
      this.cancelClose();
    });
  }

  protected scheduleOpen(): void {
    if (this.disabled()) return;
    this.cancelClose();
    this.hoverOpenTimer = setTimeout(() => this.isOpen.set(true), HOVER_OPEN_DELAY_MS);
  }

  protected scheduleClose(): void {
    this.cancelOpen();
    this.hoverCloseTimer = setTimeout(() => this.isOpen.set(false), HOVER_CLOSE_DELAY_MS);
  }

  protected keepOpen(): void {
    this.cancelClose();
  }

  protected onPanelLeave(event: MouseEvent): void {
    const target = event.relatedTarget as HTMLElement | null;
    if (target?.closest('.cdk-overlay-container')) return;
    this.scheduleClose();
  }

  private cancelOpen(): void {
    if (this.hoverOpenTimer) {
      clearTimeout(this.hoverOpenTimer);
      this.hoverOpenTimer = null;
    }
  }

  private cancelClose(): void {
    if (this.hoverCloseTimer) {
      clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = null;
    }
  }
}
